import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, Download, AlertTriangle, Edit2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatCurrency, SortableHeader, sortAndFilter } from './helpers';

export const PendingTab = ({ pending, summary, data }) => {
  const navigate = useNavigate();
  const [sort, setSort] = useState({ field: 'createdAt', dir: 'desc' });
  const [filter, setFilter] = useState('');

  const sortedEntries = sortAndFilter(pending?.entries, sort, filter, ['vendor', 'poNumber', 'type']);

  const exportToExcel = () => {
    if (!pending?.entries?.length) return;
    const exportData = pending.entries.map(entry => ({
      'PO Number': entry.poNumber, 'Vendor': entry.vendor, 'Type': entry.type,
      'Subtotal': entry.approxAmount, 'Markup %': entry.type === 'NON_BILLABLE' ? 0 : (entry.markupPct || 0),
      'Markup Amount': entry.markupAmount || 0, 'Total with Markup': entry.totalWithMarkup || entry.approxAmount,
      'Requested By': entry.requestedBy, 'Notes': entry.notes
    }));
    exportData.push({
      'PO Number': 'TOTAL', 'Vendor': '', 'Type': '', 'Subtotal': summary?.pendingPoSubtotal || 0,
      'Markup %': '', 'Markup Amount': summary?.pendingPoMarkup || 0,
      'Total with Markup': pending.total || 0, 'Requested By': '', 'Notes': ''
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pending POs');
    XLSX.writeFile(wb, `${data.job.jobNumber}_pending_pos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Card className='border-slate-200 shadow-sm rounded-sm'>
      <CardHeader>
        <div className='flex items-center justify-between flex-wrap gap-4'>
          <CardTitle className='text-xl font-bold text-slate-800'>
            Pending Material POs
            <span className='ml-2 text-lg font-normal text-amber-600 italic'>
              ~{formatCurrency(pending?.total || 0)} estimated (with markup)
            </span>
          </CardTitle>
          <div className='flex items-center gap-2'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400' />
              <Input placeholder='Search pending...' value={filter} onChange={(e) => setFilter(e.target.value)}
                className='pl-9 w-48 rounded-sm' data-testid="pending-search" />
            </div>
            <Button variant='outline' size='sm' onClick={exportToExcel} disabled={!pending?.entries?.length}
              className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white'
              data-testid="export-job-pending-excel">
              <Download className='w-4 h-4 mr-2' />Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4'>
          <p className='text-sm text-amber-800'>
            <AlertTriangle className='w-4 h-4 inline mr-1' />
            Pending amounts include estimated markup (except Non-Billable items).
            Subtotal: {formatCurrency(summary?.pendingPoSubtotal || 0)} +
            Markup: {formatCurrency(summary?.pendingPoMarkup || 0)} =
            <strong> Total: {formatCurrency(summary?.pendingPoAmount || 0)}</strong>
          </p>
        </div>
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse text-sm'>
            <thead>
              <tr className='bg-slate-100 border-b-2 border-slate-200'>
                <SortableHeader field='poNumber' label='PO #' sortState={sort} setSortState={setSort} />
                <SortableHeader field='vendor' label='Vendor' sortState={sort} setSortState={setSort} />
                <SortableHeader field='type' label='Type' sortState={sort} setSortState={setSort} />
                <SortableHeader field='approxAmount' label='Subtotal' sortState={sort} setSortState={setSort} align='right' />
                <th className='text-right p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Markup</th>
                <SortableHeader field='totalWithMarkup' label='Total' sortState={sort} setSortState={setSort} align='right' />
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Requested By</th>
                <th className='text-center p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((entry) => (
                <tr key={entry.id} className='border-b border-slate-200 hover:bg-slate-50'>
                  <td className='p-2 font-mono font-medium text-slate-700'>{entry.poNumber}</td>
                  <td className='p-2 text-slate-700'>{entry.vendor}</td>
                  <td className='p-2'>
                    <Badge className={`rounded-sm text-xs ${
                      entry.type === 'MATERIAL' ? 'bg-blue-100 text-blue-800' :
                      entry.type === 'EQUIPMENT' ? 'bg-orange-100 text-orange-800' :
                      entry.type === 'NON_BILLABLE' ? 'bg-slate-100 text-slate-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>{entry.type}</Badge>
                  </td>
                  <td className='p-2 text-right font-mono text-slate-700'>{formatCurrency(entry.approxAmount)}</td>
                  <td className='p-2 text-right font-mono text-amber-600'>
                    {entry.type === 'NON_BILLABLE' ? '-' : `${entry.markupPct || 0}%`}
                  </td>
                  <td className='p-2 text-right font-mono font-bold text-amber-600'>
                    {formatCurrency(entry.totalWithMarkup || entry.approxAmount)}
                  </td>
                  <td className='p-2 text-slate-600'>{entry.requestedBy}</td>
                  <td className='p-2 text-center'>
                    <Button variant='ghost' size='sm' onClick={() => navigate(`/material-pos?edit=${entry.id}`)}
                      className='text-[#1A3668] hover:text-[#142A52]' data-testid={`edit-pending-${entry.id}`}>
                      <Edit2 className='w-4 h-4' />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedEntries.length === 0 && (
            <div className='text-center p-8 text-slate-500'>
              {filter ? 'No matching pending POs' : 'No pending Material POs for this job'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
